import React, { useState } from 'react';
import {
  Navigation,
  MapPin,
  ChevronDown,
  Copy,
  Check,
  AlertTriangle,
  Compass,
  Radio,
} from 'lucide-react';
import { UserLocation, Language } from '../types/spot';
import { CITIES_DATA } from '../data/citiesData';

interface LocationSelectorProps {
  userLocation: UserLocation;
  radiusKm: number;
  setRadiusKm: (radius: number) => void;
  onRequestGps: () => void;
  isLocating: boolean;
  locationError: string | null;
  locationNotice: string | null;
  onManualLocationSelected: (location: UserLocation) => void;
  onLiveScan: () => void;
  isScanningLive: boolean;
  language: Language;
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
  userLocation,
  radiusKm,
  setRadiusKm,
  onRequestGps,
  isLocating,
  locationError,
  locationNotice,
  onManualLocationSelected,
  onLiveScan,
  isScanningLive,
  language,
}) => {
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [copiedAddr, setCopiedAddr] = useState(false);

  const t = {
    de: {
      walkingSuffix: ' (Gehweg)',
      allKorea: 'Ganz Korea',
      gpsLive: 'GPS Live',
      referenceHotel: 'Referenzort',
      copyAddress: 'Adresse kopieren',
      copied: 'Kopiert',
      liveScanTooltip: 'Umgebung live nach weiteren Spots scannen',
      liveScanBtn: 'Live Scan',
      scanningBtn: 'Scanne...',
      gpsBtnTooltip: 'Eigenen GPS-Standort abrufen',
      gpsActive: 'GPS Aktiv',
      gpsLiveBtn: 'GPS Ortung',
      selectAreaBtn: 'Ort wählen',
      destinationsLabel: 'Reiseziele & Stadtteile',
      radiusLabel: 'Suchradius:',
    },
    en: {
      walkingSuffix: ' (Walking)',
      allKorea: 'All Korea',
      gpsLive: 'Live GPS',
      referenceHotel: 'Reference City',
      copyAddress: 'Copy address',
      copied: 'Copied',
      liveScanTooltip: 'Scan nearby surroundings for more spots',
      liveScanBtn: 'Live Scan',
      scanningBtn: 'Scanning...',
      gpsBtnTooltip: 'Get GPS Location',
      gpsActive: 'GPS Active',
      gpsLiveBtn: 'GPS Locate',
      selectAreaBtn: 'Select Area',
      destinationsLabel: 'Destinations & Districts',
      radiusLabel: 'Search Radius:',
    },
    kr: {
      walkingSuffix: ' (도보)',
      allKorea: '전국',
      gpsLive: '실시간 GPS',
      referenceHotel: '기준 위치',
      copyAddress: '주소 복사',
      copied: '복사됨',
      liveScanTooltip: '주변 지역 실시간 추가 스캔',
      liveScanBtn: '실시간 스캔',
      scanningBtn: '스캔 중...',
      gpsBtnTooltip: 'GPS 위치 가져오기',
      gpsActive: 'GPS 활성',
      gpsLiveBtn: 'GPS 위치',
      selectAreaBtn: '지역 선택',
      destinationsLabel: '여행지 및 주요 지역',
      radiusLabel: '검색 반경:',
    },
  }[language];

  const radiusOptions = [
    { label: `500m${t.walkingSuffix}`, value: 0.5 },
    { label: '1 km', value: 1 },
    { label: '3 km', value: 3 },
    { label: '5 km', value: 5 },
    { label: '10 km', value: 10 },
    { label: '25 km', value: 25 },
    { label: '50 km', value: 50 },
    { label: '100 km', value: 100 },
    { label: t.allKorea, value: 0 },
  ];

  const primaryAddress =
    userLocation.streetAddressEn ||
    userLocation.streetAddressKr ||
    (language === 'kr'
      ? '주소 확인 중…'
      : language === 'de'
      ? 'Adresse wird ermittelt…'
      : 'Address is being resolved…');
  const koreanAddress = userLocation.streetAddressKr;

  const handleCopyAddress = () => {
    const text =
      userLocation.streetAddressEn ||
      userLocation.streetAddressKr ||
      userLocation.cityName ||
      '';
    navigator.clipboard.writeText(text);
    setCopiedAddr(true);
    setTimeout(() => setCopiedAddr(false), 2000);
  };

  return (
    <section className="location-bar">
      {/* Top Row: Location + Controls */}
      <div className="location-bar__top">
        {/* Left: Location summary */}
        <div className="location-bar__info">
          <div
            className={`location-bar__icon ${
              userLocation.isGps ? 'location-bar__icon--gps' : ''
            }`}
          >
            {userLocation.isGps ? (
              <Radio style={{ width: 18, height: 18 }} />
            ) : (
              <Compass style={{ width: 18, height: 18 }} />
            )}
          </div>

          <div className="location-bar__details">
            <div className="location-bar__city-row">
              <span className="location-bar__city">
                {userLocation.cityName || 'Seoul'}
              </span>
              <span
                className={`location-bar__badge ${
                  userLocation.isGps
                    ? 'location-bar__badge--gps'
                    : 'location-bar__badge--ref'
                }`}
              >
                {userLocation.isGps ? t.gpsLive : t.referenceHotel}
              </span>
              {userLocation.isGps && typeof userLocation.accuracy === 'number' && (
                <span className="location-bar__gps-accuracy">
                  (±{Math.round(userLocation.accuracy)} m)
                </span>
              )}
            </div>

            <div className="location-bar__address-row">
              <span className="location-bar__address" title={primaryAddress}>
                {primaryAddress}
              </span>
              <button
                onClick={handleCopyAddress}
                className="btn--inline"
                title={t.copyAddress}
                aria-label={t.copyAddress}
              >
                {copiedAddr ? (
                  <Check style={{ width: 12, height: 12, color: 'var(--success)' }} />
                ) : (
                  <Copy style={{ width: 12, height: 12 }} />
                )}
                <span className="btn-label-desktop">
                  {copiedAddr ? t.copied : t.copyAddress}
                </span>
              </button>
            </div>
            {koreanAddress && koreanAddress !== primaryAddress && (
              <span
                className="location-bar__address-kr location-bar__address-kr--desktop"
                title={koreanAddress}
              >
                {koreanAddress}
              </span>
            )}
          </div>
        </div>

        {/* Right: GPS, Live Scan & City Switcher */}
        <div className="location-bar__controls">
          {/* Live Scan Button */}
          <button
            onClick={onLiveScan}
            disabled={isScanningLive}
            className="btn btn--ghost btn--sm"
            title={t.liveScanTooltip}
          >
            <Compass
              style={{ width: 14, height: 14 }}
              className={isScanningLive ? 'animate-spin' : ''}
            />
            <span className="btn-label-desktop">
              {isScanningLive ? t.scanningBtn : t.liveScanBtn}
            </span>
            <span className="btn-label-mobile">
              {isScanningLive ? '…' : 'Scan'}
            </span>
          </button>

          {/* GPS Button */}
          <button
            onClick={onRequestGps}
            disabled={isLocating}
            className={`btn btn--sm ${
              userLocation.isGps ? 'btn--primary' : 'btn--ghost'
            }`}
            title={t.gpsBtnTooltip}
          >
            <Navigation
              style={{ width: 14, height: 14 }}
              className={isLocating ? 'animate-spin' : ''}
            />
            <span className="btn-label-desktop">
              {isLocating
                ? 'GPS...'
                : userLocation.isGps
                ? t.gpsActive
                : t.gpsLiveBtn}
            </span>
            <span className="btn-label-mobile">{isLocating ? '…' : 'GPS'}</span>
          </button>

          {/* City Dropdown */}
          <div className="dropdown">
            <button
              onClick={() => setIsCityDropdownOpen(!isCityDropdownOpen)}
              className="btn btn--ghost btn--sm"
              aria-expanded={isCityDropdownOpen}
            >
              <MapPin style={{ width: 14, height: 14 }} />
              <span className="btn-label-desktop">{t.selectAreaBtn}</span>
              <span className="btn-label-mobile">
                {language === 'kr' ? '지역' : language === 'en' ? 'Area' : 'Ort'}
              </span>
              <ChevronDown
                style={{
                  width: 12,
                  height: 12,
                  transition: 'transform 150ms',
                  transform: isCityDropdownOpen ? 'rotate(180deg)' : 'none',
                }}
              />
            </button>

            {isCityDropdownOpen && (
              <>
                <div
                  className="dropdown__overlay"
                  onClick={() => setIsCityDropdownOpen(false)}
                />
                <div className="dropdown__menu">
                  <div className="dropdown__label">{t.destinationsLabel}</div>
                  {CITIES_DATA.map((city) => (
                    <button
                      key={city.id}
                      onClick={() => {
                        onManualLocationSelected({
                          lat: city.lat,
                          lng: city.lng,
                          isGps: false,
                          cityName:
                            language === 'de'
                              ? city.nameDe
                              : language === 'kr'
                              ? city.nameKr
                              : city.name,
                          streetAddressKr:
                            city.addressKr || `${city.nameKr} 중심`,
                          streetAddressEn: `${city.name}, South Korea`,
                        });
                        setIsCityDropdownOpen(false);
                      }}
                      className="dropdown__item"
                    >
                      <span style={{ fontWeight: 600 }}>
                        {language === 'de'
                          ? city.nameDe
                          : language === 'kr'
                          ? city.nameKr
                          : city.name}
                      </span>
                      <span className="dropdown__item-kr">{city.nameKr}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Radius Bar */}
      <div className="radius-bar">
        <span className="radius-bar__label">{t.radiusLabel}</span>
        <div className="radius-bar__chips">
          {radiusOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setRadiusKm(opt.value)}
              className={`chip chip--sm ${
                radiusKm === opt.value ? 'chip--active' : ''
              }`}
            >
              <span className="leading-none">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Error or Notice */}
      {(locationError || locationNotice) && (
        <div className="alert-box">
          {locationError ? (
            <AlertTriangle
              style={{
                width: 16,
                height: 16,
                color: 'var(--error)',
                flexShrink: 0,
              }}
            />
          ) : (
            <Navigation
              style={{
                width: 16,
                height: 16,
                color: 'var(--text-secondary)',
                flexShrink: 0,
              }}
            />
          )}
          <span>{locationError || locationNotice}</span>
        </div>
      )}
    </section>
  );
};
